// EIRENE NEW YORK · redesign behaviors. Small custom elements, no dependencies.
// Prestige still owns the cart drawer, search panel, add-to-cart requests and dialogs.

const desktop = window.matchMedia('(min-width: 990px)');
const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

const define = (name, klass) => {
  if (!customElements.get(name)) customElements.define(name, klass);
};

const formatMoney = (cents) => {
  const format = window.themeVariables?.settings?.moneyFormat || '${{amount}}';
  const value = (cents / 100).toFixed(2);
  const [whole, decimals] = value.split('.');
  const group = (digits, separator) => digits.replace(/\B(?=(\d{3})+(?!\d))/g, separator);
  return format.replace(/\{\{\s*(\w+)\s*\}\}/, (_, kind) => {
    switch (kind) {
      case 'amount_no_decimals': return group(whole, ',');
      case 'amount_with_comma_separator': return `${group(whole, '.')},${decimals}`;
      case 'amount_no_decimals_with_comma_separator': return group(whole, '.');
      default: return `${group(whole, ',')}.${decimals}`;
    }
  });
};

/* Announcement bar: one message at a time, arrows and autoplay, skipping messages hidden at this width. */
define('eirene-announcement', class extends HTMLElement {
  connectedCallback() {
    this.messages = [...this.querySelectorAll('.e-announce__msg')];
    this.interval = Number(this.dataset.interval || 5) * 1000;
    this.querySelectorAll('.e-announce__nav').forEach((button) => {
      button.addEventListener('click', () => { this.step(Number(button.dataset.dir)); this.start(); });
    });
    this.addEventListener('mouseenter', () => this.stop());
    this.addEventListener('mouseleave', () => this.start());
    this.addEventListener('focusin', () => this.stop());
    this.addEventListener('focusout', () => this.start());
    desktop.addEventListener('change', () => { this.show(0); this.start(); });
    this.show(0);
    this.classList.add('is-ready');
    this.start();
  }
  disconnectedCallback() { this.stop(); }
  get eligible() {
    const hidden = desktop.matches ? 'e-show-mobile' : 'e-show-desktop';
    return this.messages.filter((message) => !message.classList.contains(hidden));
  }
  show(index) {
    const list = this.eligible;
    if (!list.length) return;
    this.index = (index + list.length) % list.length;
    this.messages.forEach((message) => message.classList.toggle('is-active', message === list[this.index]));
    this.querySelectorAll('.e-announce__nav').forEach((button) => { button.style.visibility = list.length < 2 ? 'hidden' : ''; });
  }
  step(direction) { this.show((this.index || 0) + direction); }
  start() {
    this.stop();
    if (reducedMotion.matches || this.eligible.length < 2) return;
    this.timer = setInterval(() => this.step(1), this.interval);
  }
  stop() { clearInterval(this.timer); }
});

/* Header: mega menu on hover, focus or tap, with the page dimmed underneath. */
define('eirene-header', class extends HTMLElement {
  connectedCallback() {
    this.trackStickyArea();
    this.trackOverlay();
    this.mega = document.getElementById(this.dataset.mega);
    this.trigger = this.querySelector('[data-mega-trigger]');
    if (!this.mega || !this.trigger) return;

    [this.trigger, this.mega].forEach((zone) => {
      zone.addEventListener('pointerenter', (event) => { if (event.pointerType === 'mouse') this.open(); });
      zone.addEventListener('pointerleave', (event) => { if (event.pointerType === 'mouse') this.closeSoon(); });
    });
    this.trigger.addEventListener('click', (event) => {
      // On touch screens the first tap opens the menu and the second follows the link.
      if (!this.isOpen && !window.matchMedia('(hover: hover)').matches) {
        event.preventDefault();
        this.open();
      }
    });
    this.trigger.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        this.open();
        this.mega.querySelector('a')?.focus();
      }
    });
    this.addEventListener('focusout', (event) => {
      if (!this.mega.contains(event.relatedTarget) && event.relatedTarget !== this.trigger) this.close();
    });
    this.mega.addEventListener('focusin', () => this.open());
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && this.isOpen) { this.close(); this.trigger.focus(); }
    });
    document.addEventListener('click', (event) => { if (this.isOpen && !this.contains(event.target)) this.close(); });
    desktop.addEventListener('change', () => this.close());
  }
  // Anchors (e.g. #e-reviews) and Prestige panels offset by the bars that stay on screen.
  trackStickyArea() {
    const section = this.closest('.shopify-section');
    const bar = document.querySelector('.shopify-section--eirene-announcement');
    const update = () => {
      const barSticky = bar && getComputedStyle(bar).position === 'sticky' ? bar.offsetHeight : 0;
      const headerSticky = section && getComputedStyle(section).position === 'sticky' ? section.offsetHeight : 0;
      document.documentElement.style.setProperty('--sticky-area-height', `${barSticky + headerSticky}px`);
    };
    this.stickyObserver = new ResizeObserver(update);
    [section, bar].filter(Boolean).forEach((el) => this.stickyObserver.observe(el));
    update();
  }
  // Product pages: fill the header in over the first stretch of scrolling.
  trackOverlay() {
    if (!document.documentElement.classList.contains('e-header-overlay')) return;
    const update = () => {
      this.frame = null;
      const fill = Math.min(1, Math.max(0, window.scrollY / 160));
      this.style.setProperty('--e-header-fill', fill.toFixed(3));
    };
    window.addEventListener('scroll', () => {
      if (!this.frame) this.frame = requestAnimationFrame(update);
    }, { passive: true });
    update();
  }
  get isOpen() { return this.mega.classList.contains('is-open'); }
  open() {
    clearTimeout(this.closeTimer);
    this.mega.classList.add('is-open');
    this.trigger.setAttribute('aria-expanded', 'true');
  }
  closeSoon() {
    clearTimeout(this.closeTimer);
    this.closeTimer = setTimeout(() => this.close(), 160);
  }
  close() {
    clearTimeout(this.closeTimer);
    this.mega.classList.remove('is-open');
    this.trigger.setAttribute('aria-expanded', 'false');
  }
});

/* Product rows: the progress bar under the row follows the scroll position. Rows with arrows
   ([data-e-row-prev] / [data-e-row-next], shown on desktop where a mouse can't swipe) page through the row,
   a visible width less one tile at a time; each arrow turns off at its end. */
define('eirene-carousel', class extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('.e-row__track');
    this.thumb = this.querySelector('.e-row__thumb');
    if (!this.track || !this.thumb) return;
    this.prev = this.querySelector('[data-e-row-prev]');
    this.next = this.querySelector('[data-e-row-next]');
    this.prev?.addEventListener('click', () => this.page(-1));
    this.next?.addEventListener('click', () => this.page(1));
    this.update = this.update.bind(this);
    this.track.addEventListener('scroll', this.update, { passive: true });
    this.observer = new ResizeObserver(() => { this.placeArrows(); this.update(); });
    this.observer.observe(this.track);
    this.placeArrows();
    this.update();
  }
  disconnectedCallback() { this.observer?.disconnect(); }
  // Arrows sit level with the middle of the photos: a product card's image tile, or the whole tile elsewhere.
  placeArrows() {
    if (!this.prev && !this.next) return;
    const anchor = this.track.querySelector('.e-row__item .e-card__tile') || this.track.querySelector('.e-row__item');
    if (!anchor || !anchor.offsetHeight) return;
    const top = anchor.getBoundingClientRect().top - this.track.getBoundingClientRect().top + anchor.offsetHeight / 2;
    this.style.setProperty('--e-row-arrow-top', `${Math.round(top)}px`);
  }
  page(direction) {
    const item = this.track.querySelector('.e-row__item');
    const style = getComputedStyle(this.track);
    const gap = parseFloat(style.columnGap) || 0;
    const step = item ? item.getBoundingClientRect().width + gap : this.track.clientWidth;
    const inner = this.track.clientWidth - parseFloat(style.paddingLeft) - parseFloat(style.paddingRight);
    const visible = Math.max(1, Math.round((inner + gap) / step));
    this.track.scrollBy({ left: direction * step * Math.max(1, visible - 1), behavior: reducedMotion.matches ? 'auto' : 'smooth' });
  }
  update() {
    const { scrollWidth, clientWidth, scrollLeft } = this.track;
    const overflow = scrollWidth - clientWidth;
    this.classList.toggle('is-static', overflow <= 1);
    const ratio = Math.min(1, clientWidth / scrollWidth);
    const progress = overflow > 0 ? scrollLeft / overflow : 0;
    this.thumb.style.width = `${ratio * 100}%`;
    this.thumb.style.transform = `translateX(${progress * ((1 - ratio) / ratio) * 100}%)`;
    if (this.prev) this.prev.disabled = scrollLeft <= 2;
    if (this.next) this.next.disabled = scrollLeft >= overflow - 2;
  }
});

/* Collection tiles: arrows page through the product's images. */
define('e-card-slides', class extends HTMLElement {
  connectedCallback() {
    const tile = this.closest('.e-card__tile');
    tile?.querySelectorAll('.e-card__arrow').forEach((arrow) => {
      arrow.addEventListener('click', () => {
        const width = this.clientWidth;
        const direction = Number(arrow.dataset.dir);
        const atEnd = this.scrollLeft + width >= this.scrollWidth - 2;
        const atStart = this.scrollLeft <= 2;
        if (direction > 0 && atEnd) this.scrollTo({ left: 0, behavior: 'smooth' });
        else if (direction < 0 && atStart) this.scrollTo({ left: this.scrollWidth, behavior: 'smooth' });
        else this.scrollBy({ left: direction * width, behavior: 'smooth' });
      });
    });
  }
});

/* Review strip: quotes fade from one to the next. */
define('eirene-rotator', class extends HTMLElement {
  connectedCallback() {
    this.items = [...this.children];
    if (this.items.length < 2 || reducedMotion.matches) return;
    this.index = 0;
    const tick = () => {
      this.items[this.index].classList.remove('is-active');
      this.index = (this.index + 1) % this.items.length;
      this.items[this.index].classList.add('is-active');
    };
    const start = () => {
      clearInterval(this.timer);
      this.timer = setInterval(tick, Number(this.dataset.interval || 7) * 1000);
    };
    this.addEventListener('mouseenter', () => clearInterval(this.timer));
    this.addEventListener('mouseleave', start);
    start();
  }
  disconnectedCallback() { clearInterval(this.timer); }
});

/* Product gallery on mobile: swipe, with a "1 / 6" counter and dots. */
define('e-gallery', class extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('.e-pdp__media');
    this.current = this.querySelector('[data-current]');
    this.dots = [...this.querySelectorAll('.e-pdp__dot')];
    if (!this.track || !this.dots.length) return;
    this.track.addEventListener('scroll', () => {
      if (desktop.matches) return;
      const index = Math.round(this.track.scrollLeft / this.track.clientWidth);
      if (index === this.index) return;
      this.index = index;
      if (this.current) this.current.textContent = index + 1;
      this.dots.forEach((dot, i) => dot.classList.toggle('is-active', i === index));
    }, { passive: true });
  }
});

/* Variant picker, for the few products with options (the gift card). */
define('e-variant-picker', class extends HTMLElement {
  connectedCallback() {
    this.variants = JSON.parse(this.querySelector('[data-variants]')?.textContent || '[]');
    this.form = document.getElementById(this.dataset.form);
    this.addEventListener('change', () => this.update());
  }
  update() {
    const selected = [...this.querySelectorAll('fieldset')].map((fieldset) => fieldset.querySelector('input:checked')?.value);
    this.querySelectorAll('[data-option-label]').forEach((label, i) => { label.textContent = selected[i]; });
    const variant = this.variants.find((v) => v.options.every((value, i) => value === selected[i]));
    const buttons = document.querySelectorAll(`#${this.dataset.form} [data-e-atc], [form="${this.dataset.form}"][data-e-atc]`);
    buttons.forEach((button) => { button.dataset.label ||= button.textContent.trim(); });
    if (!variant) {
      buttons.forEach((button) => { button.disabled = true; button.textContent = 'Unavailable'; });
      return;
    }
    if (this.form) this.form.querySelector('input[name="id"]').value = variant.id;
    const url = new URL(window.location.href);
    url.searchParams.set('variant', variant.id);
    window.history.replaceState({}, '', url);
    const onSale = variant.compare_at_price > variant.price;
    const saving = onSale ? Math.round(((variant.compare_at_price - variant.price) * 100) / variant.compare_at_price) : 0;
    const prices = onSale
      ? `<span class="e-price__sale">${formatMoney(variant.price)}</span><s class="e-price__compare">${formatMoney(variant.compare_at_price)}</s>`
      : `<span class="e-price__regular">${formatMoney(variant.price)}</span>`;
    document.querySelectorAll('[data-e-price]').forEach((el) => {
      el.innerHTML = prices + (onSale ? `<span class="e-label e-price__save">Save ${saving}%</span>` : '');
    });
    document.querySelectorAll('[data-e-price-sticky]').forEach((el) => { el.innerHTML = prices; });
    buttons.forEach((button) => {
      button.disabled = !variant.available;
      button.textContent = variant.available ? button.dataset.label : 'Sold out';
    });
  }
});

/* Sticky add-to-cart bar: shows once the main button has scrolled out of view (mobile). */
define('eirene-sticky-atc', class extends HTMLElement {
  connectedCallback() {
    const target = document.querySelector(this.dataset.watch);
    if (!target) return;
    this.observer = new IntersectionObserver(([entry]) => {
      const visible = !entry.isIntersecting && entry.boundingClientRect.top < 0;
      this.classList.toggle('is-visible', visible);
      // Leave room at the bottom of the page so the bar never covers the footer.
      document.documentElement.classList.toggle('e-stickybar-on', visible);
    });
    this.observer.observe(target);
    this.resizer = new ResizeObserver(() => {
      document.documentElement.style.setProperty('--e-stickybar-h', `${this.offsetHeight}px`);
    });
    this.resizer.observe(this);
  }
  disconnectedCallback() {
    this.observer?.disconnect();
    this.resizer?.disconnect();
    document.documentElement.classList.remove('e-stickybar-on');
  }
});

/* Collection grid: three or two per row on desktop, remembered per shopper. */
define('e-grid-toggle', class extends HTMLElement {
  connectedCallback() {
    this.grid = document.getElementById(this.dataset.target);
    let saved = null;
    try { saved = localStorage.getItem('eirene:grid-cols'); } catch (e) {}
    if (saved) this.set(saved);
    this.querySelectorAll('button').forEach((button) => button.addEventListener('click', () => this.set(button.dataset.cols, true)));
  }
  set(cols, remember) {
    if (!this.grid) return;
    this.grid.dataset.cols = cols;
    this.querySelectorAll('button').forEach((button) => button.setAttribute('aria-pressed', String(button.dataset.cols === cols)));
    if (remember) { try { localStorage.setItem('eirene:grid-cols', cols); } catch (e) {} }
  }
});

/* Collection on mobile: the first products, then "Load more". Desktop shows them all. */
define('e-load-more', class extends HTMLElement {
  connectedCallback() {
    this.step = Number(this.dataset.step || 8);
    this.cards = [...this.querySelectorAll('.e-grid > .e-card')];
    this.shown = this.step;
    this.button = this.querySelector('[data-load-more]');
    this.countText = this.querySelector('[data-count-text]');
    this.total = Number(this.countText?.textContent.match(/of (\d+)/)?.[1] || this.cards.length);
    this.baseTotal = this.total;
    this.allCards = this.cards;
    this.button?.addEventListener('click', () => { this.shown += this.step; this.render(); });
    this.querySelector('[data-clear-color]')?.addEventListener('click', (event) => {
      event.preventDefault();
      history.replaceState({}, '', location.pathname + location.search);
      this.applyColor();
    });
    window.addEventListener('hashchange', () => this.applyColor());
    this.applyColor();
  }
  // "Shop by color" links point at #color=Jet%20Black; each color is its own product, so filter the cards.
  applyColor() {
    const match = location.hash.match(/color=([^&]+)/);
    const color = match ? decodeURIComponent(match[1]).toLowerCase() : '';
    const notice = this.querySelector('.e-colorfilter');
    this.allCards.forEach((card) => card.classList.toggle('is-filtered', Boolean(color) && card.dataset.color !== color));
    this.cards = this.allCards.filter((card) => !card.classList.contains('is-filtered'));
    if (notice) {
      notice.hidden = !color;
      const name = notice.querySelector('[data-color-name]');
      if (name) name.textContent = match ? decodeURIComponent(match[1]) : '';
    }
    this.total = color ? this.cards.length : this.baseTotal;
    this.shown = this.step;
    this.render();
    document.querySelectorAll('.e-coloropt').forEach((option) => option.classList.toggle('is-active', option.dataset.color === color));
    document.querySelectorAll('details.e-dd[open]').forEach((details) => details.removeAttribute('open'));
  }
  render() {
    this.cards.forEach((card, i) => card.classList.toggle('is-over', i >= this.shown));
    const visible = Math.min(this.shown, this.cards.length);
    if (this.countText) this.countText.textContent = `Showing ${visible} of ${this.total}`;
    if (this.button) this.button.hidden = visible >= this.cards.length;
    const more = this.querySelector('.e-loadmore');
    if (more) more.hidden = this.cards.length <= this.step;
  }
});

/* Product page tabs: Shopify's related products, then this shopper's recently viewed. */
define('e-product-tabs', class extends HTMLElement {
  connectedCallback() {
    this.tabs = [...this.querySelectorAll('[role="tab"]')];
    this.panels = [...this.querySelectorAll('[role="tabpanel"]')];
    this.tabs.forEach((tab) => tab.addEventListener('click', () => this.select(tab)));
    this.loadRecommendations();
    this.loadViewed();
  }
  select(tab) {
    this.tabs.forEach((t) => t.setAttribute('aria-selected', String(t === tab)));
    this.panels.forEach((panel) => { panel.hidden = panel.id !== tab.getAttribute('aria-controls'); });
  }
  async fetchPart(url, selector) {
    const response = await fetch(url, { priority: 'low' });
    if (!response.ok) return null;
    const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
    return doc.querySelector(selector);
  }
  async loadRecommendations() {
    try {
      const part = await this.fetchPart(this.dataset.recommendationsUrl, '[data-e-ymal]');
      const row = part?.querySelector('eirene-carousel');
      if (row && row.querySelectorAll('.e-row__item').length >= 3) {
        this.querySelector('[data-panel="ymal"]').replaceChildren(document.importNode(row, true));
      }
    } catch (e) {}
  }
  async loadViewed() {
    let ids = [];
    try { ids = JSON.parse(localStorage.getItem('theme:recently-viewed-products') || '[]'); } catch (e) {}
    ids = ids.filter((id) => String(id) !== this.dataset.product).slice(0, Number(this.dataset.limit || 8));
    if (!ids.length) return;
    const query = ids.map((id) => `id:${id}`).join(' OR ');
    const url = `${this.dataset.searchUrl}?type=product&q=${encodeURIComponent(query)}&section_id=${this.dataset.section}`;
    try {
      const part = await this.fetchPart(url, '[data-e-viewed]');
      const row = part?.querySelector('eirene-carousel');
      if (!row || !row.querySelector('.e-row__item')) return;
      this.querySelector('[data-panel="viewed"]').replaceChildren(document.importNode(row, true));
      this.tabs.find((tab) => tab.getAttribute('aria-controls')?.includes('viewed'))?.removeAttribute('hidden');
    } catch (e) {}
  }
});

/* Reviews from every product, newest first, each card showing the product it is for.
   Reviews come from Judge.me's public widget endpoint (the same one its own widget pages with). */
const VERIFIED_TICK = '<svg class="e-allrev__tick" viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" focusable="false">'
  + '<circle cx="12" cy="12" r="11" fill="currentColor"/>'
  + '<path d="M7.2 12.4l3.2 3.2 6.4-6.6" fill="none" stroke="#ffffff" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/></svg>';
const STAR_PATH = 'M12 2.5l2.9 6.1 6.6.8-4.9 4.6 1.3 6.6L12 17.3l-5.9 3.3 1.3-6.6-4.9-4.6 6.6-.8z';
// Reviewers show as first name and last initial: "Miriam Rahmanparast" becomes "Miriam R."
const shortName = (name) => {
  const parts = name.trim().split(/\s+/);
  return parts.length > 1 ? `${parts[0]} ${parts[parts.length - 1].charAt(0).toUpperCase()}.` : parts[0];
};
const HTML_ESCAPES = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };
const escapeHtml = (value) => String(value ?? '').replace(/[&<>"']/g, (c) => HTML_ESCAPES[c]);
const htmlToText = (html) => {
  const withBreaks = String(html || '').replace(/<\/p>\s*<p[^>]*>/gi, '\n\n').replace(/<br\s*\/?>/gi, '\n');
  return (new DOMParser().parseFromString(withBreaks, 'text/html').body.textContent || '').trim();
};
const starsHtml = (rating, size) => {
  const star = `<svg class="e-icon" width="${size}" height="${size}" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="${STAR_PATH}"/></svg>`;
  const row = star.repeat(5);
  const fill = Math.max(0, Math.min(100, rating * 20));
  return `<span class="e-stars" role="img" aria-label="Rated ${rating} out of 5" style="--e-star:${size}px;--e-fill:${fill}%;">`
    + `<span class="e-stars__row" aria-hidden="true">${row}</span><span class="e-stars__row e-stars__row--fill" aria-hidden="true">${row}</span></span>`;
};
const singleStar = (size) => `<svg class="e-icon" width="${size}" height="${size}" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="${STAR_PATH}"/></svg>`;
const sizedImage = (url, width) => (url ? `${url}${url.includes('?') ? '&' : '?'}width=${width}` : '');

define('e-all-reviews', class extends HTMLElement {
  connectedCallback() {
    this.step = Number(this.dataset.step || 6);
    this.shown = this.step;
    this.list = this.querySelector('[data-list]');
    this.moreButton = this.querySelector('[data-more]');
    this.moreButton?.addEventListener('click', () => { this.shown += this.step; this.render(); });

    const summary = this.querySelector('.e-allrev__summary');
    const bars = this.querySelector('.e-allrev__bars');
    summary?.addEventListener('click', () => {
      const open = summary.getAttribute('aria-expanded') !== 'true';
      summary.setAttribute('aria-expanded', String(open));
      if (bars) bars.hidden = !open;
    });

    this.judgeme = this.closest('.e-reviews')?.querySelector('[data-judgeme]');
    this.connectWriteReview();

    // Fetch once the section is close to the screen, or after a few seconds regardless.
    const start = () => this.load();
    if ('IntersectionObserver' in window) {
      this.observer = new IntersectionObserver((entries) => {
        if (entries.some((entry) => entry.isIntersecting)) start();
      }, { rootMargin: '800px 0px' });
      this.observer.observe(this);
    }
    this.fallbackTimer = setTimeout(start, 4000);
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    this.watcher?.disconnect();
    clearTimeout(this.fallbackTimer);
  }

  // Our "Write a review" button opens Judge.me's own form, once its hidden widget has mounted.
  connectWriteReview() {
    const button = this.querySelector('[data-write-review]');
    if (!button || !this.judgeme) return;
    const find = () => this.judgeme.querySelector('.jm-action-buttons__button, .jdgm-write-rev-link');
    const enable = () => {
      if (!find()) return false;
      button.hidden = false;
      return true;
    };
    if (!enable()) {
      this.watcher = new MutationObserver(() => { if (enable()) this.watcher.disconnect(); });
      this.watcher.observe(this.judgeme, { childList: true, subtree: true });
    }
    button.addEventListener('click', () => find()?.click());
  }

  fetchPage(productId, page) {
    const params = new URLSearchParams({
      product_id: productId,
      page,
      per_page: 30,
      sort_by: 'created_at',
      sort_dir: 'desc',
      shop_domain: this.dataset.shop,
      platform: 'shopify',
    });
    return fetch(`https://cdn.judge.me/reviews/reviews_for_widget?${params}`, { priority: 'low' })
      .then((response) => (response.ok ? response.json() : null))
      .catch(() => null);
  }

  async load() {
    if (this.loading) return;
    this.loading = true;
    this.observer?.disconnect();
    clearTimeout(this.fallbackTimer);

    const sources = (this.dataset.sources || '').split(',').filter(Boolean).map((entry) => {
      const [id, count] = entry.split(':');
      return { id, pages: Math.max(1, Math.ceil(Number(count) / 30)) };
    });
    // Judge.me's store reviews, when the theme counts them, come with the page as JSON.
    let shopReviews = [];
    try { shopReviews = JSON.parse(this.querySelector('[data-shop-reviews]')?.textContent || '[]'); } catch (e) {}
    if (!Array.isArray(shopReviews)) shopReviews = [];
    if (!sources.length && !shopReviews.length) return;

    const pages = await Promise.all(sources.flatMap(({ id, pages: total }) => (
      Array.from({ length: total }, (_, i) => this.fetchPage(id, i + 1))
    )));
    // Reviews listed under "Reviews to hide" in theme settings are left out.
    const seen = new Set((this.dataset.hidden || '').split(',').map((id) => id.trim()).filter(Boolean));
    this.reviews = [];
    for (const review of [...pages.flatMap((page) => page?.reviews || []), ...shopReviews]) {
      if (!review || seen.has(review.uuid)) continue;
      seen.add(review.uuid);
      this.reviews.push(review);
    }
    this.reviews.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

    if (!this.reviews.length || (sources.length && !pages.some(Boolean))) {
      // Judge.me unreachable: show its own widget rather than an empty section.
      this.list.innerHTML = '';
      this.judgeme?.classList.add('is-fallback');
      return;
    }
    this.renderBars();
    this.render();
  }

  renderBars() {
    const target = this.querySelector('[data-bars]');
    if (!target) return;
    const total = this.reviews.length;
    target.innerHTML = [5, 4, 3, 2, 1].map((stars) => {
      const count = this.reviews.filter((review) => Math.round(review.rating) === stars).length;
      const width = total ? Math.round((count / total) * 100) : 0;
      return `<div class="e-allrev__row"><span class="e-allrev__row-stars">${stars}${singleStar(10)}</span>`
        + `<span class="e-allrev__bar"><span style="width:${width}%"></span></span>`
        + `<span class="e-allrev__row-count">${count}</span></div>`;
    }).join('');
  }

  card(review) {
    const name = review.is_anonymous_reviewer || !review.reviewer_name ? 'Anonymous' : shortName(review.reviewer_name);
    const date = new Date(review.created_at).toLocaleDateString('en-US');
    const body = htmlToText(review.body_html || review.body);
    const photos = (review.pictures_urls || []).map((picture) => (
      `<a class="e-allrev__photo" href="${escapeHtml(picture.huge || picture.original)}" target="_blank" rel="noopener">`
      + `<img src="${escapeHtml(picture.compact || picture.small)}" alt="Photo from ${escapeHtml(name)}" loading="lazy" width="160" height="160"></a>`
    )).join('');
    const productTitle = review.product_title_localized || review.product_title;
    const productUrl = /^\/(?!\/)/.test(review.product_url || '') ? review.product_url : '';
    const productImage = sizedImage(review.product_variant_image_url, 160);
    const product = review.is_shop_review
      ? '<span class="e-allrev__product"><span>Store review</span></span>'
      : productTitle
      ? `<a class="e-allrev__product"${productUrl ? ` href="${escapeHtml(productUrl)}"` : ''}>`
        + (productImage ? `<img src="${escapeHtml(productImage)}" alt="" loading="lazy" width="80" height="100">` : '')
        + `<span>${escapeHtml(productTitle)}</span></a>`
      : '';
    return `<article class="e-allrev__card">
      <div class="e-allrev__who"><span class="e-allrev__name">${escapeHtml(name)}</span>${review.verified_buyer ? `<span class="e-allrev__verified">Verified Buyer${VERIFIED_TICK}</span>` : ''}</div>
      <span class="e-allrev__date">${escapeHtml(date)}</span>
      ${starsHtml(Number(review.rating) || 0, 16)}
      ${review.title ? `<p class="e-allrev__title">${escapeHtml(review.title)}</p>` : ''}
      ${body ? `<p class="e-allrev__body">${escapeHtml(body)}</p>` : ''}
      ${photos ? `<div class="e-allrev__photos">${photos}</div>` : ''}
      ${product}
    </article>`;
  }

  render() {
    this.list.innerHTML = this.reviews.slice(0, this.shown).map((review) => this.card(review)).join('');
    if (this.moreButton) this.moreButton.hidden = this.shown >= this.reviews.length;
  }
});

/* Color filter options (collection page): filter the grid without a page load. */
document.addEventListener('click', (event) => {
  const option = event.target.closest?.('.e-coloropt');
  if (!option) return;
  event.preventDefault();
  const url = new URL(window.location.href);
  url.hash = option.dataset.color ? `color=${encodeURIComponent(option.textContent.trim())}` : '';
  history.replaceState({}, '', url.pathname + url.search + url.hash);
  document.querySelector('e-load-more')?.applyColor();
});

/* Filters and sorting apply as soon as a choice changes. */
document.addEventListener('change', (event) => {
  const input = event.target.closest?.('[data-autosubmit]');
  if (!input?.form) return;
  if (input.form.requestSubmit) input.form.requestSubmit();
  else input.form.submit();
});

/* Product page accordions: opening one closes the others. */
document.addEventListener('toggle', (event) => {
  const details = event.target;
  if (!(details instanceof HTMLDetailsElement) || !details.open || !details.classList.contains('e-acc')) return;
  details.closest('.e-accs')?.querySelectorAll('details.e-acc[open]').forEach((other) => {
    if (other !== details) other.open = false;
  });
}, true);

/* One filter dropdown open at a time; a click outside closes it. */
document.addEventListener('click', (event) => {
  document.querySelectorAll('details.e-dd[open]').forEach((details) => {
    if (!details.contains(event.target)) details.removeAttribute('open');
  });
});
document.addEventListener('toggle', (event) => {
  const details = event.target;
  if (!(details instanceof HTMLDetailsElement) || !details.open || !details.classList.contains('e-dd')) return;
  document.querySelectorAll('details.e-dd[open]').forEach((other) => { if (other !== details) other.removeAttribute('open'); });
}, true);

/* Our Story video: plays silently until the visitor turns the sound on. */
document.addEventListener('click', (event) => {
  const button = event.target.closest('[data-e-sound]');
  const video = button?.parentElement.querySelector('video');
  if (!video) return;
  video.muted = !video.muted;
  if (!video.muted) video.play().catch(() => {});
  button.setAttribute('aria-pressed', String(!video.muted));
  button.querySelector('[data-e-sound-label]').textContent = video.muted ? 'Sound on' : 'Sound off';
});

/* App widgets rendered after load, so this runs whenever the page changes:
   - Klaviyo sign-up popup: the button reads "Subscribe" and there is no "No thanks", and the teaser label
     reads just the amount ("Receive $30 off your first order" becomes "$30 off"). Only those exact texts.
   - OuterSignal trust badge (product pages): plain text, not a link. Its link only led to OuterSignal's
     own site; the badge re-sets the link only while it exists, so removing it sticks. eirene.css hides
     its "Learn more" and blocks clicks. */
let appFrame = 0;
const tidyAppWidgets = () => {
  appFrame = 0;
  document.querySelectorAll('[id^="os-badge-"] a[href]').forEach((link) => {
    ['href', 'target', 'rel', 'role', 'tabindex'].forEach((name) => link.removeAttribute(name));
  });
  document.querySelectorAll('[data-testid="POPUP"] .klaviyo-form-button').forEach((button) => {
    const label = button.textContent.trim();
    if (label === 'Sign up') button.textContent = 'Subscribe';
    if (label === 'No thanks') (button.closest('[data-testid="form-row"]') || button).style.display = 'none';
  });
  document.querySelectorAll('[class*="kl-teaser-"] [data-testid="animated-teaser"]').forEach((teaser) => {
    const walker = document.createTreeWalker(teaser, NodeFilter.SHOW_TEXT);
    while (walker.nextNode()) {
      const offer = walker.currentNode.data.trim().match(/^Receive (\$\d+) off your first order$/);
      if (offer) walker.currentNode.data = `${offer[1]} off`;
    }
  });
};
new MutationObserver(() => {
  if (!appFrame) appFrame = requestAnimationFrame(tidyAppWidgets);
}).observe(document.body, { childList: true, subtree: true });

/* Sign-up page form: our first and last name fields above Klaviyo's embedded form (email and button).
   The email is noted as the form is sent; once Klaviyo shows its thank-you step (the email field is gone),
   the names go onto that Klaviyo profile. Also gives Klaviyo's button this page's own label. */
define('eirene-signup-form', class extends HTMLElement {
  connectedCallback() {
    const note = () => {
      const email = this.querySelector('form input[type="email"]')?.value.trim();
      if (email) this.email = email;
    };
    this.addEventListener('click', (event) => { if (event.target.closest('.klaviyo-form-button')) note(); }, true);
    this.addEventListener('submit', note, true);
    this.addEventListener('keydown', (event) => { if (event.key === 'Enter') note(); }, true);
    this.observer = new MutationObserver(() => this.update());
    this.observer.observe(this, { childList: true, subtree: true, characterData: true });
    this.update();
  }

  disconnectedCallback() {
    this.observer?.disconnect();
  }

  update() {
    const form = this.querySelector('form');
    if (!form) return;
    const label = this.dataset.buttonText;
    const button = form.querySelector('input[type="email"]') && form.querySelector('.klaviyo-form-button');
    if (label && button && button.textContent.trim() !== label) button.textContent = label;
    if (this.saved || !this.email || form.querySelector('input[type="email"]')) return;
    this.saved = true;
    // Tells Shopify's customer events that this sign-up went through (data-event names it; a custom pixel in
    // Settings > Customer events passes it on to Meta). Not on button clicks, so failed attempts don't count.
    if (this.dataset.event) {
      try { window.Shopify?.analytics?.publish?.(this.dataset.event, { form: this.dataset.form || '' }); } catch (error) { /* tracking only */ }
    }
    const first = this.querySelector('[name="first_name"]')?.value.trim() || '';
    const last = this.querySelector('[name="last_name"]')?.value.trim() || '';
    if (!first && !last) return;
    // Klaviyo's client profile API, so the names fill the profile's own First and Last name. klaviyo.identify
    // saved them as custom properties called first_name and last_name instead. Only the names given, so an
    // empty field never clears a name Klaviyo already has.
    const attributes = { email: this.email };
    if (first) attributes.first_name = first;
    if (last) attributes.last_name = last;
    const script = document.querySelector('script[src*="klaviyo.js"][src*="company_id="]');
    const company = script && new URL(script.src).searchParams.get('company_id');
    if (!company) return;
    fetch(`https://a.klaviyo.com/client/profiles/?company_id=${encodeURIComponent(company)}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', revision: '2026-07-15' },
      body: JSON.stringify({ data: { type: 'profile', attributes } }),
    }).catch(() => { /* the sign-up itself already went through Klaviyo */ });
  }
});

/* Countdown on the sign-up page: days, hours, minutes and seconds to data-end. Hidden while the date is
   missing or unreadable, and once it has passed. A date without a time zone is New York time in winter
   (UTC-5), which covers November and December. */
define('eirene-countdown', class extends HTMLElement {
  connectedCallback() {
    let value = (this.dataset.end || '').trim().replace(' ', 'T');
    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) value += 'T00:00';
    if (value && !/(Z|[+-]\d{2}:?\d{2})$/.test(value)) value += '-05:00';
    this.end = Date.parse(value);
    if (Number.isNaN(this.end)) return;
    this.parts = ['d', 'h', 'm', 's'].map((key) => this.querySelector(`[data-${key}]`));
    this.tick();
    this.timer = setInterval(() => this.tick(), 1000);
  }

  disconnectedCallback() {
    clearInterval(this.timer);
  }

  tick() {
    const left = Math.floor((this.end - Date.now()) / 1000);
    if (left <= 0) {
      this.hidden = true;
      clearInterval(this.timer);
      return;
    }
    // Without a days unit (the password page), the hours run past 24.
    const days = this.parts[0] ? Math.floor(left / 86400) : 0;
    const values = [days, Math.floor((left - days * 86400) / 3600), Math.floor((left % 3600) / 60), left % 60];
    values.forEach((value, i) => { if (this.parts[i]) this.parts[i].textContent = i ? String(value).padStart(2, '0') : value; });
    this.hidden = false;
  }
});

/* Password page card: the password field, and "Unlock access now" swapping it for the sign-up form (the back
   link swaps it back). A link ending in #unlock opens the form straight away. */
define('eirene-unlock', class extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', (event) => {
      if (event.target.closest('[data-unlock-open]')) this.show('signup');
      else if (event.target.closest('[data-unlock-back]')) this.show('password');
    });
    if (window.location.hash === '#unlock' && this.querySelector('[data-step="signup"]')) this.show('signup', false);
  }

  // On a click the new step fades in and takes the focus; on page load (#unlock) it just shows.
  show(name, byClick = true) {
    this.querySelectorAll('[data-step]').forEach((step) => {
      const active = step.dataset.step === name;
      step.hidden = !active;
      step.classList.toggle('is-entering', active && byClick && !reducedMotion.matches);
    });
    this.querySelector('[data-unlock-open]')?.setAttribute('aria-expanded', String(name === 'signup'));
    if (!byClick) return;
    const step = this.querySelector(`[data-step="${name}"]`);
    const field = name === 'signup' ? step?.querySelector('input:not([type="hidden"])') : step?.querySelector('input[type="password"]');
    field?.focus({ preventScroll: true });
  }
});

/* Customer video tile in "Eirene On You": silent and looping, it loads nothing until it nears the screen and
   plays only while visible. */
define('eirene-clip', class extends HTMLElement {
  connectedCallback() {
    const video = this.querySelector('video');
    if (!video) return;
    if (!('IntersectionObserver' in window)) {
      video.play().catch(() => {});
      return;
    }
    this.observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) video.play().catch(() => {});
      else video.pause();
    }, { rootMargin: '200px 0px' });
    this.observer.observe(this);
  }

  disconnectedCallback() {
    this.observer?.disconnect();
  }
});
